/**
 * In-memory demo data store.
 *
 * Seeded from the typed fixtures in ./fixtures at construction (structuredClone'd
 * so the fixture consts stay pristine). The demo interceptor reads/writes this
 * store so CRUD actions taken during a demo session (invite a team member, create
 * an app connection, delete something) persist for the rest of the session. A
 * hard page refresh re-instantiates the singleton, which doubles as "reset demo
 * data" — acceptable and arguably desirable for a demo.
 */

import { Injectable } from '@angular/core';
import { UserResponse } from '../../api/models/user-response';
import { TeamInvitationResponse } from '../../services/team-management.service';
import { DEMO_ADMIN_USER, DEMO_TEAM_MEMBERS } from './fixtures/users.fixtures';
import { DEMO_CUSTOMER_PROFILE, DEMO_INVITATIONS } from './fixtures/organization.fixtures';
import { CustomerProfileResponse } from '../../api/models/customer-profile-response';
import { AppResponse } from '../../api/models/app-response';
import { AppCreateRequest } from '../../api/models/app-create-request';
import { AppUpdateRequest } from '../../api/models/app-update-request';
import { DEMO_APPS } from './fixtures/apps.fixtures';
import { ReviewResponse } from '../../api/models/review-response';
import { DEMO_REVIEWS } from './fixtures/reviews.fixtures';
import { AiInsight, AiInsightType } from '../../services/ai-insight.service';
import { DEMO_AI_INSIGHTS, buildGeneratedInsight } from './fixtures/ai-insights.fixtures';

@Injectable({ providedIn: 'root' })
export class DemoStore {
  adminUser: UserResponse = structuredClone(DEMO_ADMIN_USER);
  teamMembers: UserResponse[] = structuredClone(DEMO_TEAM_MEMBERS);
  customerProfile: CustomerProfileResponse = structuredClone(DEMO_CUSTOMER_PROFILE);
  invitations: TeamInvitationResponse[] = structuredClone(DEMO_INVITATIONS);
  apps: AppResponse[] = structuredClone(DEMO_APPS);
  reviews: ReviewResponse[] = structuredClone(DEMO_REVIEWS);
  aiInsights: AiInsight[] = structuredClone(DEMO_AI_INSIGHTS);

  private nextUserId = Math.max(...this.teamMembers.map((m) => m.id ?? 0)) + 1;
  private nextInvitationId = Math.max(...this.invitations.map((i) => i.id), 5000) + 1;
  private nextAppId = Math.max(...this.apps.map((a) => a.id ?? 0)) + 1;

  // ---------------------------------------------------------------- ai insights

  generateInsight(appId: number, type: AiInsightType): AiInsight {
    const insight = buildGeneratedInsight(appId, type);
    this.aiInsights = [insight, ...this.aiInsights.filter((i) => !(i.appId === appId && i.insightType === type))];
    return insight;
  }

  // ---------------------------------------------------------------- apps

  addApp(req: AppCreateRequest): AppResponse {
    const now = new Date().toISOString();
    const app: AppResponse = {
      ...req,
      id: this.nextAppId++,
      isActive: true,
      customerProfileId: this.customerProfile.id,
      customerProfileName: this.customerProfile.organizationName,
      createdByEmail: this.adminUser.email,
      createdByUserId: this.adminUser.id,
      googlePlayConnectionStatus: req.googlePlayServiceAccountKey ? 'READY' : 'NOT_CONFIGURED',
      firebaseConnectionStatus: req.firebaseServiceAccountKey ? 'READY' : 'NOT_CONFIGURED',
      createdAt: now,
      updatedAt: now
    };
    this.apps.push(app);
    return app;
  }

  updateApp(id: number, req: AppUpdateRequest): AppResponse | undefined {
    const app = this.apps.find((a) => a.id === id);
    if (!app) {
      return undefined;
    }
    Object.assign(app, req, { updatedAt: new Date().toISOString() });
    return app;
  }

  deleteApp(id: number): boolean {
    const before = this.apps.length;
    this.apps = this.apps.filter((a) => a.id !== id);
    return this.apps.length < before;
  }

  activateApp(id: number): AppResponse | undefined {
    const app = this.apps.find((a) => a.id === id);
    if (!app) {
      return undefined;
    }
    app.isActive = !app.isActive;
    app.updatedAt = new Date().toISOString();
    return app;
  }

  syncGooglePlay(appId: number): AppResponse | undefined {
    const app = this.apps.find((a) => a.id === appId);
    if (!app) {
      return undefined;
    }
    app.googlePlayConnectionStatus = 'SYNCED';
    app.googlePlayConnectionLastError = undefined;
    app.googlePlayLastSync = new Date().toISOString();
    app.lastSync = app.googlePlayLastSync;
    return app;
  }

  syncFirebase(appId: number): AppResponse | undefined {
    const app = this.apps.find((a) => a.id === appId);
    if (!app) {
      return undefined;
    }
    app.firebaseConnectionStatus = 'SYNCED';
    app.firebaseConnectionLastError = undefined;
    app.firebaseLastSync = new Date().toISOString();
    app.lastSync = app.firebaseLastSync;
    return app;
  }

  // ---------------------------------------------------------------- team / invitations

  addInvitation(email: string, role: 'CUSTOMER_ADMIN' | 'CUSTOMER_USER'): TeamInvitationResponse {
    const invitation: TeamInvitationResponse = {
      id: this.nextInvitationId++,
      email,
      role,
      status: 'PENDING',
      invitedByEmail: this.adminUser.email,
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
    };
    this.invitations.push(invitation);
    return invitation;
  }

  removeTeamMember(userId: number): void {
    this.teamMembers = this.teamMembers.filter((m) => m.id !== userId);
    this.customerProfile.currentUsersCount = this.teamMembers.length;
  }

  canAddUsers(): boolean {
    const max = this.customerProfile.maxUsers;
    if (max == null) {
      return true;
    }
    return this.teamMembers.length < max;
  }

  updateDataSharingOptIn(optIn: boolean): void {
    this.customerProfile.dataSharingOptIn = optIn;
    if (this.adminUser.customerProfile) {
      this.adminUser.customerProfile.dataSharingOptIn = optIn;
    }
  }
}
